import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Chip, FormControlLabel, Switch, MenuItem
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  Save as SaveIcon, Cancel as CancelIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

function TargetsManagement() {
  const [targets, setTargets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    channel: '',
    contentType: '',
    weeklyCount: 0,
    monthlyCount: 0,
    isActive: true
  });
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchTargets();
    fetchDropdowns();
  }, []);

  const fetchTargets = async () => {
    try {
      const response = await axios.get(`${API_URL}/targets`);
      setTargets(response.data.data.targets || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching targets:', error);
      setAlert({ type: 'error', message: 'Error loading targets' });
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const response = await axios.get(`${API_URL}/dropdowns`);
      const data = response.data.data;
      setChannels(data.channels || []);
      setContentTypes(data.contentTypes || []);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  const handleAdd = () => {
    setEditItem(null);
    setFormData({
      channel: '',
      contentType: '',
      weeklyCount: 0,
      monthlyCount: 0,
      isActive: true
    });
    setOpenDialog(true);
  };

  const handleEdit = (channel, contentType, weeklyTarget, monthlyTarget) => {
    // Edit both weekly and monthly targets together
    setEditItem({ channel, contentType, weeklyTarget, monthlyTarget });
    setFormData({
      channel,
      contentType,
      weeklyCount: weeklyTarget?.targetCount || 0,
      monthlyCount: monthlyTarget?.targetCount || 0,
      isActive: weeklyTarget?.isActive ?? monthlyTarget?.isActive ?? true
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const promises = [];
      
      if (editItem) {
        // Update mode - update both weekly and monthly
        if (formData.weeklyCount > 0) {
          if (editItem.weeklyTarget) {
            // Update existing weekly target
            promises.push(
              axios.put(`${API_URL}/targets/${editItem.weeklyTarget.id}`, {
                targetCount: formData.weeklyCount,
                isActive: formData.isActive
              })
            );
          } else {
            // Create new weekly target
            promises.push(
              axios.post(`${API_URL}/targets`, {
                channel: formData.channel,
                contentType: formData.contentType,
                targetType: 'weekly',
                targetCount: formData.weeklyCount,
                isActive: formData.isActive
              })
            );
          }
        } else if (editItem.weeklyTarget) {
          // Delete weekly target if count is 0
          promises.push(axios.delete(`${API_URL}/targets/${editItem.weeklyTarget.id}`));
        }

        if (formData.monthlyCount > 0) {
          if (editItem.monthlyTarget) {
            // Update existing monthly target
            promises.push(
              axios.put(`${API_URL}/targets/${editItem.monthlyTarget.id}`, {
                targetCount: formData.monthlyCount,
                isActive: formData.isActive
              })
            );
          } else {
            // Create new monthly target
            promises.push(
              axios.post(`${API_URL}/targets`, {
                channel: formData.channel,
                contentType: formData.contentType,
                targetType: 'monthly',
                targetCount: formData.monthlyCount,
                isActive: formData.isActive
              })
            );
          }
        } else if (editItem.monthlyTarget) {
          // Delete monthly target if count is 0
          promises.push(axios.delete(`${API_URL}/targets/${editItem.monthlyTarget.id}`));
        }
      } else {
        // Create mode - create both if counts > 0
        if (formData.weeklyCount > 0) {
          promises.push(
            axios.post(`${API_URL}/targets`, {
              channel: formData.channel,
              contentType: formData.contentType,
              targetType: 'weekly',
              targetCount: formData.weeklyCount,
              isActive: formData.isActive
            })
          );
        }
        if (formData.monthlyCount > 0) {
          promises.push(
            axios.post(`${API_URL}/targets`, {
              channel: formData.channel,
              contentType: formData.contentType,
              targetType: 'monthly',
              targetCount: formData.monthlyCount,
              isActive: formData.isActive
            })
          );
        }
      }

      await Promise.all(promises);
      setAlert({ type: 'success', message: editItem ? 'Targets updated successfully' : 'Targets created successfully' });
      setOpenDialog(false);
      fetchTargets();
    } catch (error) {
      console.error('Error saving targets:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'Error saving targets'
      });
    }
  };

  const handleDelete = async (channel, contentType, weeklyTarget, monthlyTarget) => {
    if (!window.confirm(`Are you sure you want to delete all targets for ${channel} - ${contentType}?`)) return;

    try {
      const promises = [];
      if (weeklyTarget) promises.push(axios.delete(`${API_URL}/targets/${weeklyTarget.id}`));
      if (monthlyTarget) promises.push(axios.delete(`${API_URL}/targets/${monthlyTarget.id}`));
      
      await Promise.all(promises);
      setAlert({ type: 'success', message: 'Targets deleted successfully' });
      fetchTargets();
    } catch (error) {
      console.error('Error deleting targets:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'Error deleting targets'
      });
    }
  };

  // Group targets by channel
  const groupedTargets = targets.reduce((acc, target) => {
    if (!acc[target.channel]) {
      acc[target.channel] = [];
    }
    acc[target.channel].push(target);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading targets...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {alert && (
        <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Channel</strong></TableCell>
              <TableCell><strong>Content Type</strong></TableCell>
              <TableCell align="center"><strong>Weekly Target</strong></TableCell>
              <TableCell align="center"><strong>Monthly Target</strong></TableCell>
              <TableCell align="center"><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {targets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">No targets configured</Typography>
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(groupedTargets).flatMap(([channel, channelTargets]) => {
                // Group by contentType within each channel
                const byContentType = channelTargets.reduce((acc, target) => {
                  if (!acc[target.contentType]) {
                    acc[target.contentType] = { weekly: null, monthly: null };
                  }
                  acc[target.contentType][target.targetType] = target;
                  return acc;
                }, {});

                return Object.entries(byContentType).map(([contentType, typeTargets]) => (
                  <TableRow key={`${channel}-${contentType}`} hover>
                    <TableCell>{channel}</TableCell>
                    <TableCell>{contentType}</TableCell>
                    <TableCell align="center">
                      {typeTargets.weekly ? (
                        <Chip
                          label={typeTargets.weekly.targetCount}
                          size="small"
                          color="primary"
                          sx={{ minWidth: 40 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {typeTargets.monthly ? (
                        <Chip
                          label={typeTargets.monthly.targetCount}
                          size="small"
                          color="secondary"
                          sx={{ minWidth: 40 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          (typeTargets.weekly?.isActive || typeTargets.monthly?.isActive)
                            ? 'Active'
                            : 'Inactive'
                        }
                        color={
                          (typeTargets.weekly?.isActive || typeTargets.monthly?.isActive)
                            ? 'success'
                            : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(channel, contentType, typeTargets.weekly, typeTargets.monthly)}
                        color="primary"
                        title="Edit Targets"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(channel, contentType, typeTargets.weekly, typeTargets.monthly)}
                        color="error"
                        title="Delete Targets"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ));
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        Total: {Object.keys(groupedTargets).reduce((sum, channel) => {
          const types = groupedTargets[channel].reduce((acc, t) => {
            if (!acc[t.contentType]) acc[t.contentType] = true;
            return acc;
          }, {});
          return sum + Object.keys(types).length;
        }, 0)} target configurations
      </Typography>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editItem ? `Edit Targets: ${editItem.channel} - ${editItem.contentType}` : 'Add New Targets'}
        </DialogTitle>
        <DialogContent>
          <Box pt={2} display="flex" flexDirection="column" gap={3}>
            <TextField
              label="Channel"
              select
              fullWidth
              value={formData.channel}
              onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              required
              disabled={!!editItem}
            >
              {channels.map((channel) => (
                <MenuItem key={channel} value={channel}>
                  {channel}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Content Type"
              select
              fullWidth
              value={formData.contentType}
              onChange={(e) => setFormData({ ...formData, contentType: e.target.value })}
              required
              disabled={!!editItem}
            >
              {contentTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Weekly Target"
              type="number"
              fullWidth
              value={formData.weeklyCount}
              onChange={(e) =>
                setFormData({ ...formData, weeklyCount: parseInt(e.target.value) || 0 })
              }
              InputProps={{ inputProps: { min: 0 } }}
              helperText="Minimum weekly deliverables (set to 0 to remove)"
            />

            <TextField
              label="Monthly Target"
              type="number"
              fullWidth
              value={formData.monthlyCount}
              onChange={(e) =>
                setFormData({ ...formData, monthlyCount: parseInt(e.target.value) || 0 })
              }
              InputProps={{ inputProps: { min: 0 } }}
              helperText="Minimum monthly deliverables (set to 0 to remove)"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={
              !formData.channel || 
              !formData.contentType || 
              (formData.weeklyCount === 0 && formData.monthlyCount === 0)
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TargetsManagement;
